import React, { useState } from "react";
import { mergeEnterpriseCaseStudyFarms } from "../data/enterpriseCaseStudyContent";
import PublicFarmPreview from "../components/enterprise/PublicFarmPreview";
import NDVITimeseriesChart from "../components/enterprise/PublicFarmPreview/NDVITimeseriesChart";
import YieldHistoryChart from "../components/enterprise/PublicFarmPreview/YieldHistoryChart";
import TransformationTimeline from "../components/enterprise/PublicFarmPreview/TransformationTimeline";
import FieldsGrid from "../components/enterprise/PublicFarmPreview/FieldsGrid";
import IrrigationStats from "../components/enterprise/PublicFarmPreview/IrrigationStats";
import FinanceSummary from "../components/enterprise/PublicFarmPreview/FinanceSummary";
import FarmStatusBanner from "../components/enterprise/PublicFarmPreview/FarmStatusBanner";
import EN from "../data/en.json";

/**
 * PreviewVisualizations
 *
 * Dev-only page for testing and previewing all case study visualization
 * components with real farm data before integration. Accessible at /preview.
 *
 * Shows two modes:
 * 1. Full orchestrated PublicFarmPreview per farm
 * 2. Individual component breakdown for each farm
 */

const allFarms = mergeEnterpriseCaseStudyFarms(EN.Enterprise?.caseStudies?.farms || []);
const farmsWithPreview = allFarms.filter((f) => f.preview && !f.lightCase);

const PreviewVisualizations = () => {
  const [mode, setMode] = useState("full"); // "full" | "breakdown"
  const [selectedFarmIdx, setSelectedFarmIdx] = useState(0);
  const [isArabic, setIsArabic] = useState(false);

  const farm = farmsWithPreview[selectedFarmIdx];
  if (!farm) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">No farms with preview data found. Check EN.Enterprise.caseStudies.farms path.</p>
      </div>
    );
  }
  const preview = farm.preview || {};

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header controls */}
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm px-4 py-3">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-3">
          <h1 className="text-lg font-bold text-slate-900 mr-4">
            🔬 Visualization Preview
          </h1>

          {/* Farm selector */}
          <div className="flex gap-1">
            {farmsWithPreview.map((f, i) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setSelectedFarmIdx(i)}
                className={`px-3 py-1.5 text-xs font-bold rounded-full transition-all ${
                  i === selectedFarmIdx
                    ? "bg-emerald-600 text-white shadow-md"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {f.name}
              </button>
            ))}
          </div>

          {/* Mode toggle */}
          <div className="flex gap-1 ml-auto">
            <button
              type="button"
              onClick={() => setMode("full")}
              className={`px-3 py-1.5 text-xs font-bold rounded-full ${
                mode === "full"
                  ? "bg-blue-600 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              Full Preview
            </button>
            <button
              type="button"
              onClick={() => setMode("breakdown")}
              className={`px-3 py-1.5 text-xs font-bold rounded-full ${
                mode === "breakdown"
                  ? "bg-blue-600 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              Component Breakdown
            </button>
          </div>

          {/* Arabic toggle */}
          <button
            type="button"
            onClick={() => setIsArabic(!isArabic)}
            className={`px-3 py-1.5 text-xs font-bold rounded-full ${
              isArabic
                ? "bg-amber-500 text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {isArabic ? "العربية ✓" : "AR"}
          </button>
        </div>
      </div>

      {/* Data Audit Panel */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="rounded-xl bg-white border border-slate-200 p-4 mb-6 shadow-sm">
          <h2 className="text-sm font-bold text-slate-700 mb-3">
            📋 Data Audit — {farm.name}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 text-xs">
            <DataCheck label="Preview Object" ok={!!farm.preview} />
            <DataCheck label="Fields" ok={preview.fields?.length > 0} detail={`${preview.fields?.length || 0} fields`} />
            <DataCheck label="NDVI Timeseries" ok={preview.ndviTimeseries?.length > 0} detail={`${preview.ndviTimeseries?.length || 0} series`} />
            <DataCheck label="Yield History" ok={preview.yieldHistory?.length > 0} detail={`${preview.yieldHistory?.length || 0} seasons`} />
            <DataCheck label="Irrigation" ok={!!preview.irrigation} />
            <DataCheck label="Finance" ok={!!preview.finance} />
            <DataCheck label="NDVI Snapshots" ok={preview.ndviSnapshots?.length > 0} detail={`${preview.ndviSnapshots?.length || 0} snapshots`} />
            <DataCheck label="Journey" ok={farm.journey?.length > 0} detail={`${farm.journey?.length || 0} phases`} />
            <DataCheck label="Public Preview ID" ok={!!farm.publicPreviewId} />
          </div>
        </div>

        {/* Full mode: orchestrated component */}
        {mode === "full" && farm && (
          <div className="space-y-8">
            <SectionLabel title="PublicFarmPreview — Full Orchestrated Component" />
            <PublicFarmPreview
              farm={farm}
              preview={farm.preview}
              isArabic={isArabic}
              showCta={true}
            />
          </div>
        )}

        {/* Breakdown mode: individual components */}
        {mode === "breakdown" && farm && (
          <div className="space-y-8">
            <SectionLabel title="1. FarmStatusBanner" />
            <FarmStatusBanner farm={farm} isArabic={isArabic} />

            <SectionLabel title="2. FieldsGrid" />
            <FieldsGrid fields={preview.fields} isArabic={isArabic} />

            <SectionLabel title="3. NDVITimeseriesChart" />
            {preview.ndviTimeseries?.length > 0 ? (
              <NDVITimeseriesChart ndviTimeseries={preview.ndviTimeseries} isArabic={isArabic} />
            ) : (
              <EmptyState label="No NDVI timeseries data" />
            )}

            <SectionLabel title="4. YieldHistoryChart" />
            {preview.yieldHistory?.length > 0 ? (
              <YieldHistoryChart yieldHistory={preview.yieldHistory} isArabic={isArabic} />
            ) : (
              <EmptyState label="No yield history data" />
            )}

            <SectionLabel title="5. IrrigationStats" />
            {preview.irrigation ? (
              <IrrigationStats irrigation={preview.irrigation} isArabic={isArabic} />
            ) : (
              <EmptyState label="No irrigation data" />
            )}

            <SectionLabel title="6. FinanceSummary" />
            {preview.finance ? (
              <FinanceSummary finance={preview.finance} isArabic={isArabic} />
            ) : (
              <EmptyState label="No finance data" />
            )}

            <SectionLabel title="7. TransformationTimeline" />
            {farm.journey?.length > 0 ? (
              <TransformationTimeline
                timeline={farm.journey}
                ndviSnapshots={preview.ndviSnapshots}
                isArabic={isArabic}
              />
            ) : (
              <EmptyState label="No journey/timeline data" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const SectionLabel = ({ title }) => (
  <div className="flex items-center gap-3">
    <div className="h-px flex-1 bg-slate-200" />
    <span className="text-xs font-bold uppercase tracking-widest text-slate-400 whitespace-nowrap">
      {title}
    </span>
    <div className="h-px flex-1 bg-slate-200" />
  </div>
);

const DataCheck = ({ label, ok, detail }) => (
  <div className={`rounded-lg p-2 border ${ok ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-0.5">{label}</span>
    <span className={`text-xs font-bold ${ok ? "text-emerald-700" : "text-red-600"}`}>
      {ok ? "✓" : "✗"} {detail || (ok ? "Present" : "Missing")}
    </span>
  </div>
);

const EmptyState = ({ label }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
    <span className="text-sm text-slate-400">{label}</span>
  </div>
);

export default PreviewVisualizations;
